import React from "react";
import { useCallback } from "react";
import { Nullable } from "sonobello.utilities.react";

import { IMedicalFormConclusions } from "../../Types/IMedicalFormConclusions";
import { IMedicalFormItemProps } from "../../Types/IMedicalFormItemProps";
import MedicalFormItemState from "../../Types/MedicalFormItemState";
import MedicalFormState from "../../Types/MedicalFormState";

export interface ISideEffectMedicalFormItemDecoratorConfig {
  readonly setMedicalConclusions: (
    stateMutator: (oldState: IMedicalFormConclusions) => IMedicalFormConclusions
  ) => void;
  /** An optional mutator function called when a child form item changes which returns the new state of the medical
   * form conclusions data.
   * @remarks This is useful for enforcing inter-form-item logic, such as clearing a conclusion value based on
   * another form item's value. Note that this is also called for child form items of the decorated item, so event
   * filtering by id is likely necessary.
   * @param previousFormState The previous state of the medical form data.
   * @param newItemState The new state of the decorated form item, or one of its children.
   * @returns The new state of the medical form data, or null if no change is needed.
   */
  readonly transformConclusions: Nullable<
    (
      previousFormState: IMedicalFormConclusions,
      newItemState: MedicalFormItemState
    ) => Nullable<IMedicalFormConclusions>
  >;
  /** An optional mutator function called when the form is updated which returns the new state of the medical form.
   * @remarks This is useful for enforcing inter-form-item logic, such as clearing a form value if another form item
   * disables it. Note that this is also called for child form items of the decorated item, so event filtering by
   * id is likely necessary.
   * @param previousFormState The previous state of the medical form.
   * @param newItemState The new state of the decorated form item, or one of its children.
   * @returns The state of the medical form item to update, or null if no change is needed.
   */
  readonly transformFormState: Nullable<
    (previousFormState: MedicalFormState, newItemState: MedicalFormItemState) => Nullable<MedicalFormState>
  >;
  /** The decorated form item component to be rendered. */
  FormItem: React.FC<IMedicalFormItemProps>;
}

/**
 * A composable decorator for a child {@link IMedicalFormItemProps} component which allows for customization
 * of the application or repeal of a note tag based on the form item's value and a provided configuration.
 */
const SideEffectMedicalFormItemDecorator: React.FC<
  IMedicalFormItemProps & ISideEffectMedicalFormItemDecoratorConfig
> = ({
  setMedicalFormState,
  setMedicalConclusions,
  transformFormState,
  transformConclusions,
  onChange,
  FormItem,
  ...rest
}) => {
  const handleChange = useCallback(
    (value: MedicalFormItemState) => {
      if (transformConclusions) setMedicalConclusions(oldState => transformConclusions(oldState, value) ?? oldState);
      if (transformFormState) setMedicalFormState(oldState => transformFormState(oldState, value) ?? oldState);
      onChange(value);
    },
    [onChange, setMedicalConclusions, transformConclusions]
  );
  return <FormItem {...rest} setMedicalFormState={setMedicalFormState} onChange={handleChange} />;
};

export default SideEffectMedicalFormItemDecorator;
